import { useMemo } from 'react';
import { useCatalogDataContextEventsTransformedSelector } from 'src/contexts/CatalogDataContext';
import {
  NO_GROUP_ID,
  useMultiSelectionContextItemSelectionSelector,
} from 'src/contexts/MultiSelectionContext';
import { packNumericIds } from 'src/contexts/MultiSelectionContext/MultiSelectionContext.utils';
import {
  EventWithData,
  IListingGroupItem,
  ListingGroup,
} from 'src/WebApiController';

const filterListingsBySelectedIds = <T extends IListingGroupItem>(
  listings: T[],
  selectedIds: Set<string>
): T[] =>
  listings
    .map((l) => {
      if (selectedIds.has(l.id.toString())) {
        return l;
      }
      const groupItems = (l as unknown as ListingGroup).groupItems;
      if (groupItems?.length) {
        const selectedGroupItems = filterListingsBySelectedIds(
          groupItems,
          selectedIds
        );
        if (selectedGroupItems.length) {
          return { ...l, groupItems: selectedGroupItems };
        }
      }
      return null;
    })
    .filter((l): l is T => l != null);

/**
 * Returns the events from CatalogDataContext that have any selection in
 * MultiSelectionContext, with their entities narrowed down to only the
 * selected ones. A fully-selected event (group checkbox) keeps all its
 * entities; a partially-selected event keeps only the selected
 * listings/sales/ticket-groups. Note that `counts` are left as the original
 * server-side counts and are not adjusted to the filtered entities.
 *
 * Supports both grouped views, where selection is keyed by event virtId, and
 * flattened views, where selection is keyed by NO_GROUP_ID and resolved by
 * matching the selected entity ids against each event's entities.
 */
export const useSelectedEvents = (): EventWithData[] => {
  const itemSelection = useMultiSelectionContextItemSelectionSelector();
  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();

  return useMemo(() => {
    if (!eventsTransformed?.length) {
      return [];
    }

    const flattenedSelection = itemSelection[NO_GROUP_ID];
    if (
      flattenedSelection?.isGroupSelected &&
      !flattenedSelection.items.length
    ) {
      // Select-all in flattened view
      return eventsTransformed;
    }
    const flattenedItemIds = flattenedSelection?.items ?? [];

    return eventsTransformed.reduce<EventWithData[]>((acc, ev) => {
      const groupSelection = itemSelection[ev.event.virtId];

      if (groupSelection?.isGroupSelected && !groupSelection.items.length) {
        // The whole event is selected
        acc.push(ev);
        return acc;
      }

      const selectedIds = new Set<string>([
        ...(groupSelection?.items ?? []),
        ...flattenedItemIds,
      ]);
      if (!selectedIds.size) {
        return acc;
      }

      const { listings, sales, ticketGroups } = ev.entities;
      const selectedListings =
        listings && filterListingsBySelectedIds(listings, selectedIds);
      const selectedSales =
        sales && sales.filter((s) => selectedIds.has(s.id.toString()));
      const selectedTicketGroups =
        ticketGroups &&
        ticketGroups.filter(
          (tg) =>
            selectedIds.has(tg.id.toString()) ||
            selectedIds.has(packNumericIds([tg.id, tg.tgId ?? 0]))
        );

      if (
        !selectedListings?.length &&
        !selectedSales?.length &&
        !selectedTicketGroups?.length
      ) {
        return acc;
      }

      acc.push({
        ...ev,
        entities: {
          ...ev.entities,
          listings: selectedListings,
          sales: selectedSales,
          ticketGroups: selectedTicketGroups,
        },
      });
      return acc;
    }, []);
  }, [eventsTransformed, itemSelection]);
};
